// src/pages/ContactPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useForm }         from 'react-hook-form';
import { useThemePalette } from '../hooks/useThemePalette';
import { useThemeStore }   from '../stores/useThemeStore';

type FormData = {
  name:    string;
  email:   string;
  subject: string;
  message: string;
};

// ── Shared SVG stroke props ───────────────────────────────────────────────────
const SI = {
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: '1.9',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const SUBJECTS = [
  { value: 'general',     label: 'General Inquiry'          },
  { value: 'technical',   label: 'Technical Support'        },
  { value: 'premium',     label: 'Premium Access & Payment' },
  { value: 'partnership', label: 'Partnership Opportunity'  },
  { value: 'feedback',    label: 'Feedback & Suggestions'   },
  { value: 'other',       label: 'Other'                    },
];

const TEAM = [
  {
    initials: 'MK',
    photo:    '/images/Kilanava.webp' as string | undefined,
    name:     'Mariam Kilanava',
    role:     'Head of the Project',
    email:    'm.kilanava@sportenglish.com',
    linkedin: 'https://ge.linkedin.com/in/mariam-kilanava-586670218',
    facebook: 'https://www.facebook.com/mkilanava',
  },
  {
    initials: 'MK',
    photo:    undefined as string | undefined,
    name:     'Maia Khurtsilava',
    role:     'Co-Head of the Project',
    email:    'm.khurtsilava@sportenglish.com',
    linkedin: undefined as string | undefined,
    facebook: undefined as string | undefined,
  },
];

const INFO_CARDS = [
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
    title: 'Response Time',
    body:  'We typically respond within 24–48 hours during business days',
    accentIdx: 0,
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="10" r="3"/><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/></svg>,
    title: 'Location',
    body:  'Based in Tbilisi, Georgia\nWorking with partners worldwide',
    accentIdx: 1,
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>,
    title: 'Support',
    body:  'For technical issues, please include your browser and device details',
    accentIdx: 2,
  },
  {
    icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
    title: 'Partnerships',
    body:  'Interested institutions can contact us for collaboration opportunities',
    accentIdx: 3,
  },
];

// ── Accent purple (matches HomePage) ─────────────────────────────────────────
const PURPLE = '#4f3ff5';

// ── Keyframe injection ────────────────────────────────────────────────────────
(function injectContactStyles() {
  if (typeof document === 'undefined') return;
  const id = 'esp-contact-kf';
  if (document.getElementById(id)) return;
  const s = document.createElement('style');
  s.id = id;
  s.textContent = `
    @keyframes esp-spin        { to { transform: rotate(360deg); } }
    @keyframes contactReveal   { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
    @keyframes contactFadeIn   { from { opacity:0; } to { opacity:1; } }
    @keyframes contactSlideUp  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
    @keyframes successSlide    { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
    @keyframes particleDriftC  {
      0%   { transform: translate(0, 0) scale(1);   opacity: var(--p-opacity); }
      33%  { transform: translate(var(--px), var(--py)) scale(1.5); opacity: calc(var(--p-opacity) * 1.7); }
      66%  { transform: translate(calc(var(--px) * -0.6), calc(var(--py) * 1.4)) scale(0.75); opacity: calc(var(--p-opacity) * 0.5); }
      100% { transform: translate(0, 0) scale(1);   opacity: var(--p-opacity); }
    }
    @keyframes meshFloat1C {
      0%, 100% { transform: translate(0, 0) scale(1); }
      33%      { transform: translate(-45px, 60px) scale(1.08); }
      66%      { transform: translate(34px, -40px) scale(0.93); }
    }
    @keyframes meshFloat2C {
      0%, 100% { transform: translate(0, 0) scale(1); }
      40%      { transform: translate(55px, -50px) scale(1.12); }
      70%      { transform: translate(-28px, 38px) scale(0.91); }
    }
    @keyframes glowPulse {
      0%, 100% { opacity: 0.5; }
      50%      { opacity: 1.0; }
    }
    @keyframes orbitRingC {
      from { transform: translate(-50%, -50%) rotate(0deg); }
      to   { transform: translate(-50%, -50%) rotate(360deg); }
    }
    @keyframes ecPulseC1 {
      0%   { stroke-dashoffset:  200;  opacity: 0;   }
      6%   { opacity: 0.9; }
      82%  { opacity: 0.9; }
      92%  { stroke-dashoffset: -980;  opacity: 0;   }
      100% { stroke-dashoffset: -980;  opacity: 0;   }
    }
    @keyframes ecPulseC2 {
      0%   { stroke-dashoffset:  160;  opacity: 0;   }
      8%   { opacity: 0.7; }
      84%  { opacity: 0.7; }
      94%  { stroke-dashoffset: -920;  opacity: 0;   }
      100% { stroke-dashoffset: -920;  opacity: 0;   }
    }
    @keyframes cardFloat {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-5px); }
    }
    .esp-success-banner { animation: successSlide 0.35s ease both; }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
      }
    }
  `;
  document.head.appendChild(s);
})();

// ── Particle field for the header ─────────────────────────────────────────────
function ContactParticles({ isDark }: { isDark: boolean }) {
  const particles = React.useMemo(() => {
    const rng = (min: number, max: number) => min + Math.random() * (max - min);
    return Array.from({ length: 22 }, (_, i) => ({
      id:   i,
      left: `${rng(0, 100)}%`,
      top:  `${rng(0, 100)}%`,
      size: rng(1, 3),
      opacity: rng(0.05, 0.22),
      dur:  `${rng(9, 20)}s`,
      delay:`${rng(0, 10)}s`,
      px:   `${rng(-28, 28)}px`,
      py:   `${rng(-28, 28)}px`,
    }));
  }, []);

  return (
    <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {particles.map(p => (
        <div
          key={p.id}
          style={{
            position:     'absolute',
            left:         p.left,
            top:          p.top,
            width:        `${p.size}px`,
            height:       `${p.size}px`,
            borderRadius: '50%',
            background:   isDark
              ? 'radial-gradient(circle, rgba(168,133,247,0.9), rgba(79,140,247,0.5))'
              : 'radial-gradient(circle, rgba(79,63,245,0.7), rgba(79,140,247,0.4))',
            boxShadow: isDark ? `0 0 ${p.size * 2.5}px rgba(168,133,247,0.6)` : 'none',
            ['--p-opacity' as never]: p.opacity,
            ['--px' as never]:        p.px,
            ['--py' as never]:        p.py,
            animation: `particleDriftC ${p.dur} ease-in-out ${p.delay} infinite`,
            opacity:   p.opacity,
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
}

// ── Header energy curves ──────────────────────────────────────────────────────
function HeaderEnergyCurves({ C }: { C: ReturnType<typeof useThemePalette> }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 1200 360"
      preserveAspectRatio="xMidYMid slice"
      style={{
        position:      'absolute',
        inset:         0,
        width:         '100%',
        height:        '100%',
        pointerEvents: 'none',
        zIndex:        0,
        overflow:      'hidden',
      }}
    >
      <defs>
        <linearGradient id="hec-g1" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor={C.accent}  stopOpacity="0"   />
          <stop offset="35%"  stopColor={C.accent}  stopOpacity="0.9" />
          <stop offset="65%"  stopColor={C.accent2} stopOpacity="1"   />
          <stop offset="100%" stopColor={PURPLE}     stopOpacity="0"   />
        </linearGradient>
        <linearGradient id="hec-g2" x1="0" y1="1" x2="1" y2="0">
          <stop offset="0%"   stopColor={PURPLE}     stopOpacity="0"   />
          <stop offset="40%"  stopColor={C.accent3}  stopOpacity="0.8" />
          <stop offset="100%" stopColor={C.accent}   stopOpacity="0"   />
        </linearGradient>
      </defs>
      {/* Curve 1 — glow layer + core */}
      <path d="M -50 300 C 200 80, 500 320, 750 160 S 1000 20, 1250 200"
        fill="none" stroke="url(#hec-g1)" strokeWidth="20" strokeLinecap="round"
        strokeDasharray="200 9999" opacity="0.22"
        style={{ animation: 'ecPulseC1 9s ease-in-out infinite', willChange: 'stroke-dashoffset, opacity' }} />
      <path d="M -50 300 C 200 80, 500 320, 750 160 S 1000 20, 1250 200"
        fill="none" stroke="url(#hec-g1)" strokeWidth="2" strokeLinecap="round"
        strokeDasharray="200 9999"
        style={{ animation: 'ecPulseC1 9s ease-in-out infinite', willChange: 'stroke-dashoffset, opacity' }} />
      {/* Curve 2 */}
      <path d="M 1250 60 C 950 260, 650 20, 400 180 S 100 360, -50 120"
        fill="none" stroke="url(#hec-g2)" strokeWidth="14" strokeLinecap="round"
        strokeDasharray="160 9999" opacity="0.20"
        style={{ animation: 'ecPulseC2 13s ease-in-out 3s infinite', willChange: 'stroke-dashoffset, opacity' }} />
      <path d="M 1250 60 C 950 260, 650 20, 400 180 S 100 360, -50 120"
        fill="none" stroke="url(#hec-g2)" strokeWidth="1.5" strokeLinecap="round"
        strokeDasharray="160 9999"
        style={{ animation: 'ecPulseC2 13s ease-in-out 3s infinite', willChange: 'stroke-dashoffset, opacity' }} />
    </svg>
  );
}

// ── useScrollReveal hook ──────────────────────────────────────────────────────
function useScrollReveal() {
  const [inView, setInView] = useState<Record<string, boolean>>({});
  const refs = useRef<Record<string, HTMLElement | null>>({});

  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    Object.entries(refs.current).forEach(([key, el]) => {
      if (!el) return;
      const obs = new IntersectionObserver(
        ([entry]) => { if (entry.isIntersecting) { setInView(prev => ({ ...prev, [key]: true })); obs.disconnect(); } },
        { threshold: 0.12 },
      );
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach(o => o.disconnect());
  }, []);

  const setRef = (key: string) => (el: HTMLElement | null) => { refs.current[key] = el; };
  const reveal = (key: string, delay = 0): React.CSSProperties => ({
    opacity:    inView[key] ? 1 : 0,
    transform:  inView[key] ? 'translateY(0)' : 'translateY(22px)',
    transition: `opacity 0.55s ease ${delay}ms, transform 0.55s ease ${delay}ms`,
  });

  return { inView, setRef, reveal };
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ContactPage() {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';

  const [width,        setWidth]        = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const [submitted,    setSubmitted]    = useState(false);
  const [submitting,   setSubmitting]   = useState(false);
  const [hoveredInfo,  setHoveredInfo]  = useState<number | null>(null);
  const [hoveredTeam,  setHoveredTeam]  = useState<number | null>(null);
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [submitHov,    setSubmitHov]    = useState(false);

  const { setRef, reveal } = useScrollReveal();

  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);

  const isMobile = width < 768;
  const isTablet = width < 960;

  const { register, handleSubmit, formState: { errors }, reset } = useForm<FormData>();

  const regName    = register('name',    { required: 'Name is required',          minLength: { value: 2, message: 'Name must be at least 2 characters' } });
  const regEmail   = register('email',   { required: 'Email is required',         pattern:   { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Please enter a valid email' } });
  const regSubject = register('subject', { required: 'Please select a subject'    });
  const regMessage = register('message', { required: 'Message is required',       minLength: { value: 20, message: 'Please write at least 20 characters' } });

  const onSubmit = (_data: FormData) => {
    setSubmitting(true);
    setTimeout(() => { setSubmitting(false); setSubmitted(true); reset(); }, 900);
  };

  // ── Glass card base ─────────────────────────────────────────────────────────
  const glassCard = (hovered = false, extraStyle: React.CSSProperties = {}): React.CSSProperties => ({
    background:           isDark
      ? hovered ? 'rgba(20,28,52,0.82)' : 'rgba(13,18,32,0.72)'
      : hovered ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.86)',
    backdropFilter:       'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border:               `1px solid ${hovered
      ? (isDark ? 'rgba(124,111,247,0.38)' : 'rgba(79,63,245,0.22)')
      : (isDark ? 'rgba(255,255,255,0.09)' : 'rgba(0,0,0,0.07)')}`,
    borderRadius:         '20px',
    boxShadow:            hovered
      ? isDark
        ? '0 0 0 1px rgba(124,111,247,0.15), 0 20px 50px rgba(0,0,0,0.40), 0 0 40px rgba(79,63,245,0.10)'
        : '0 0 0 1px rgba(79,63,245,0.10), 0 20px 50px rgba(0,0,0,0.10)'
      : isDark
        ? '0 8px 40px rgba(0,0,0,0.30)'
        : '0 8px 40px rgba(0,0,0,0.06)',
    transition:           'border-color 0.22s ease, box-shadow 0.22s ease, background 0.22s ease, transform 0.22s ease',
    ...extraStyle,
  });

  // ── Input base ──────────────────────────────────────────────────────────────
  const inputBase = (fieldName: string, hasError: boolean): React.CSSProperties => ({
    width:           '100%',
    padding:         '12px 16px',
    border:          `1.5px solid ${hasError ? C.error : focusedField === fieldName
      ? (isDark ? 'rgba(124,111,247,0.70)' : C.accent)
      : (isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)')}`,
    borderRadius:    '12px',
    background:      focusedField === fieldName
      ? (isDark ? 'rgba(79,63,245,0.08)' : 'rgba(79,63,245,0.04)')
      : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.90)'),
    color:           C.text,
    fontSize:        '0.95rem',
    fontFamily:      'system-ui, -apple-system, "Segoe UI", sans-serif',
    transition:      'border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
    outline:         'none',
    boxShadow:       focusedField === fieldName
      ? `0 0 0 3px ${isDark ? 'rgba(124,111,247,0.20)' : 'rgba(79,63,245,0.14)'}, 0 2px 12px rgba(79,63,245,0.10)`
      : hasError ? `0 0 0 3px rgba(239,68,68,0.14)` : 'none',
    boxSizing:       'border-box',
  });

  // ── Section padding ─────────────────────────────────────────────────────────
  const sectionStyle: React.CSSProperties = {
    maxWidth: '1280px',
    margin:   '0 auto',
    padding:  isMobile ? '60px 20px' : '80px 40px',
  };

  // ── Info card icon accent colors ────────────────────────────────────────────
  const iconAccents = [C.accent, C.accent2, C.accent3, C.accent];

  return (
    <div style={{
      minHeight:   '100vh',
      background:  C.bg,
      fontFamily:  'system-ui, -apple-system, "Segoe UI", sans-serif',
      transition:  'background 0.25s ease',
      overflowX:   'hidden',
    }}>

      {/* ── Page Header ─────────────────────────────────────────────────────── */}
      <div style={{
        position:   'relative',
        overflow:   'hidden',
        background: isDark
          ? 'linear-gradient(160deg, rgba(8,12,24,1) 0%, rgba(16,22,50,1) 60%, rgba(22,14,50,1) 100%)'
          : 'linear-gradient(160deg, rgba(248,250,252,1) 0%, rgba(232,241,255,1) 60%, rgba(237,232,255,1) 100%)',
        borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.06)'}`,
        padding: isMobile ? '64px 20px 60px' : '88px 40px 80px',
        textAlign: 'center',
      }}>
        {/* Animated particles */}
        <ContactParticles isDark={isDark} />

        {/* Energy curves */}
        <HeaderEnergyCurves C={C} />

        {/* Gradient mesh orbs */}
        <div aria-hidden="true" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', zIndex: 0 }}>
          <div style={{
            position:   'absolute',
            top:        '-120px',
            left:       '-80px',
            width:      '500px',
            height:     '500px',
            borderRadius: '50%',
            background:   isDark
              ? 'radial-gradient(ellipse, rgba(79,63,245,0.18) 0%, transparent 68%)'
              : 'radial-gradient(ellipse, rgba(79,63,245,0.10) 0%, transparent 68%)',
            animation:  'meshFloat1C 18s ease-in-out infinite',
            willChange: 'transform',
          }} />
          <div style={{
            position:   'absolute',
            bottom:     '-100px',
            right:      '-60px',
            width:      '420px',
            height:     '420px',
            borderRadius: '50%',
            background:   isDark
              ? 'radial-gradient(ellipse, rgba(79,140,247,0.15) 0%, transparent 65%)'
              : 'radial-gradient(ellipse, rgba(79,140,247,0.09) 0%, transparent 65%)',
            animation:  'meshFloat2C 22s ease-in-out 4s infinite',
            willChange: 'transform',
          }} />
          {/* Subtle orbit ring */}
          <div style={{
            position:     'absolute',
            top:          '50%',
            left:         '50%',
            width:        isMobile ? '320px' : '480px',
            height:       isMobile ? '320px' : '480px',
            borderRadius: '50%',
            border:       `1px solid ${isDark ? 'rgba(79,63,245,0.12)' : 'rgba(79,63,245,0.08)'}`,
            animation:    'orbitRingC 40s linear infinite',
            willChange:   'transform',
          }} />
        </div>

        {/* Header content */}
        <div style={{ position: 'relative', zIndex: 1, animation: 'contactReveal 0.65s ease both' }}>
          <span style={{
            display:       'inline-flex',
            alignItems:    'center',
            gap:           '6px',
            background:    isDark ? 'rgba(79,63,245,0.16)' : 'rgba(79,63,245,0.10)',
            color:         C.accent,
            border:        `1px solid ${isDark ? 'rgba(124,111,247,0.35)' : 'rgba(79,63,245,0.22)'}`,
            borderRadius:  '50px',
            padding:       '5px 14px',
            fontSize:      '0.75rem',
            fontWeight:    700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            marginBottom:  '20px',
            boxShadow:     isDark ? '0 0 20px rgba(79,63,245,0.20)' : 'none',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" {...SI}>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Get in Touch
          </span>

          <h1 style={{
            fontSize:             isMobile ? '2.4rem' : '3.4rem',
            fontWeight:           900,
            lineHeight:           1.12,
            letterSpacing:        '-0.03em',
            margin:               '0 0 20px',
            background:           `linear-gradient(135deg, ${C.text} 0%, ${C.accent} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor:  'transparent',
            backgroundClip:       'text',
          }}>
            Contact{' '}
            <span style={{
              background:           `linear-gradient(135deg, ${C.accent} 0%, ${C.accent2} 100%)`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor:  'transparent',
              backgroundClip:       'text',
            }}>
              START
            </span>
          </h1>

          <p style={{
            fontSize:   '1.1rem',
            color:      C.muted,
            maxWidth:   '580px',
            margin:     '0 auto',
            lineHeight: 1.75,
          }}>
            Get in touch with our team for inquiries, support, or partnership opportunities
          </p>
        </div>
      </div>

      {/* ── Main Section ────────────────────────────────────────────────────── */}
      <section style={sectionStyle}>

        {/* Section intro */}
        <div
          ref={setRef('intro')}
          style={{ marginBottom: '40px', ...reveal('intro') }}
        >
          <span style={{
            display:       'inline-flex',
            alignItems:    'center',
            gap:           '6px',
            background:    isDark ? 'rgba(79,63,245,0.14)' : 'rgba(79,63,245,0.08)',
            color:         C.accent,
            border:        `1px solid ${isDark ? 'rgba(124,111,247,0.30)' : 'rgba(79,63,245,0.20)'}`,
            borderRadius:  '50px',
            padding:       '5px 14px',
            fontSize:      '0.75rem',
            fontWeight:    700,
            letterSpacing: '0.09em',
            textTransform: 'uppercase',
            marginBottom:  '14px',
          }}>
            <svg width="13" height="13" viewBox="0 0 24 24" {...SI}>
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
            Get in Touch
          </span>

          <h2 style={{
            fontSize:             'clamp(1.85rem, 4vw, 2.6rem)',
            fontWeight:           900,
            lineHeight:           1.2,
            letterSpacing:        '-0.025em',
            margin:               '0 0 16px',
            background:           `linear-gradient(135deg, ${C.text} 0%, ${C.accent} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor:  'transparent',
            backgroundClip:       'text',
          }}>
            We'd Love to Hear From You
          </h2>

          <p style={{
            fontSize:   '1.05rem',
            color:      C.muted,
            maxWidth:   '640px',
            lineHeight: 1.75,
            margin:     0,
          }}>
            Have questions about our materials, need technical support, or interested in collaborating?
            We're here to help and will respond as soon as possible.
          </p>
        </div>

        {/* ── Success Banner ─────────────────────────────────────────────────── */}
        {submitted && (
          <div
            className="esp-success-banner"
            role="alert"
            aria-live="assertive"
            style={{
              display:      'flex',
              alignItems:   'center',
              gap:          '16px',
              background:   isDark ? 'rgba(52,211,153,0.10)' : 'rgba(16,185,129,0.08)',
              border:       `1px solid ${isDark ? 'rgba(52,211,153,0.30)' : 'rgba(16,185,129,0.30)'}`,
              borderRadius: '14px',
              padding:      '18px 24px',
              marginBottom: '32px',
              boxShadow:    isDark ? '0 4px 24px rgba(52,211,153,0.08)' : 'none',
            }}
          >
            <div style={{
              width:          '40px',
              height:         '40px',
              borderRadius:   '50%',
              flexShrink:     0,
              background:     isDark ? 'rgba(52,211,153,0.18)' : 'rgba(52,211,153,0.14)',
              border:         `1px solid ${isDark ? 'rgba(52,211,153,0.35)' : 'rgba(52,211,153,0.30)'}`,
              display:        'flex',
              alignItems:     'center',
              justifyContent: 'center',
              color:          C.success,
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                <polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <div>
              <strong style={{ color: C.success, fontSize: '0.97rem' }}>Message sent successfully!</strong>
              <p style={{ color: C.muted, fontSize: '0.9rem', margin: '3px 0 0', lineHeight: 1.5 }}>
                Thank you for contacting us. We'll get back to you within 24–48 hours.
              </p>
            </div>
          </div>
        )}

        {/* ── Two-col grid: Form + Team ────────────────────────────────────── */}
        <div style={{
          display:             'grid',
          gridTemplateColumns: isMobile || isTablet ? '1fr' : '1fr 1fr',
          gap:                 '28px',
          alignItems:          'start',
          marginBottom:        '52px',
        }}>

          {/* ── Contact Form ──────────────────────────────────────────────── */}
          <div
            ref={setRef('form')}
            style={{
              ...glassCard(),
              padding:    isMobile ? '28px 20px' : '40px',
              ...reveal('form', 60),
            }}
          >
            <h3 style={{
              fontSize:    '1.25rem',
              fontWeight:  800,
              color:       C.text,
              margin:      '0 0 30px',
              display:     'flex',
              alignItems:  'center',
              gap:         '12px',
              letterSpacing: '-0.01em',
            }}>
              <span style={{
                width:          '38px',
                height:         '38px',
                borderRadius:   '11px',
                flexShrink:     0,
                background:     `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                color:          '#fff',
                boxShadow:      `0 4px 14px rgba(79,63,245,0.35)`,
              }}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="22" y1="2" x2="11" y2="13"/>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                </svg>
              </span>
              Send a Message
            </h3>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              {/* Name */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '7px', fontSize: '0.875rem', fontWeight: 700, color: C.text, letterSpacing: '0.01em' }}>
                  Your Name <span style={{ color: C.error }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter your full name"
                  style={inputBase('name', !!errors.name)}
                  onFocus={() => setFocusedField('name')}
                  {...regName}
                  onBlur={(e) => { setFocusedField(null); regName.onBlur(e); }}
                />
                {errors.name && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '0.8rem', color: C.error }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                    {errors.name.message}
                  </span>
                )}
              </div>

              {/* Email */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '7px', fontSize: '0.875rem', fontWeight: 700, color: C.text, letterSpacing: '0.01em' }}>
                  Email Address <span style={{ color: C.error }}>*</span>
                </label>
                <input
                  type="email"
                  placeholder="your.email@example.com"
                  style={inputBase('email', !!errors.email)}
                  onFocus={() => setFocusedField('email')}
                  {...regEmail}
                  onBlur={(e) => { setFocusedField(null); regEmail.onBlur(e); }}
                />
                {errors.email && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '0.8rem', color: C.error }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                    {errors.email.message}
                  </span>
                )}
              </div>

              {/* Subject */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '7px', fontSize: '0.875rem', fontWeight: 700, color: C.text, letterSpacing: '0.01em' }}>
                  Subject <span style={{ color: C.error }}>*</span>
                </label>
                <select
                  style={{
                    ...inputBase('subject', !!errors.subject),
                    appearance:        'none',
                    backgroundImage:   `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%238b9cc4' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`,
                    backgroundRepeat:  'no-repeat',
                    backgroundPosition:'right 16px center',
                    paddingRight:      '40px',
                    cursor:            'pointer',
                  }}
                  onFocus={() => setFocusedField('subject')}
                  {...regSubject}
                  onBlur={(e) => { setFocusedField(null); regSubject.onBlur(e); }}
                >
                  <option value="">Select a subject</option>
                  {SUBJECTS.map(s => (
                    <option
                      key={s.value}
                      value={s.value}
                      style={{ background: isDark ? '#0d1220' : '#fff', color: C.text }}
                    >
                      {s.label}
                    </option>
                  ))}
                </select>
                {errors.subject && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '0.8rem', color: C.error }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                    {errors.subject.message}
                  </span>
                )}
              </div>

              {/* Message */}
              <div style={{ marginBottom: '28px' }}>
                <label style={{ display: 'block', marginBottom: '7px', fontSize: '0.875rem', fontWeight: 700, color: C.text, letterSpacing: '0.01em' }}>
                  Your Message <span style={{ color: C.error }}>*</span>
                </label>
                <textarea
                  rows={6}
                  placeholder="Please provide details about your inquiry..."
                  style={{ ...inputBase('message', !!errors.message), resize: 'vertical', minHeight: '140px' }}
                  onFocus={() => setFocusedField('message')}
                  {...regMessage}
                  onBlur={(e) => { setFocusedField(null); regMessage.onBlur(e); }}
                />
                {errors.message && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '6px', fontSize: '0.8rem', color: C.error }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><circle cx="12" cy="16" r="0.5" fill="currentColor"/></svg>
                    {errors.message.message}
                  </span>
                )}
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={submitting}
                onMouseEnter={() => setSubmitHov(true)}
                onMouseLeave={() => setSubmitHov(false)}
                style={{
                  width:          '100%',
                  padding:        '14px',
                  background:     submitting
                    ? (isDark ? 'rgba(79,140,247,0.50)' : 'rgba(79,140,247,0.55)')
                    : submitHov
                      ? `linear-gradient(135deg, ${C.accent2}, ${C.accent})`
                      : `linear-gradient(135deg, ${C.accent}, ${C.accent2})`,
                  color:          '#fff',
                  border:         'none',
                  borderRadius:   '12px',
                  fontWeight:     700,
                  fontSize:       '1rem',
                  fontFamily:     'system-ui, -apple-system, "Segoe UI", sans-serif',
                  cursor:         submitting ? 'not-allowed' : 'pointer',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  gap:            '8px',
                  transform:      !submitting && submitHov ? 'translateY(-2px)' : 'translateY(0)',
                  boxShadow:      submitting
                    ? 'none'
                    : submitHov
                      ? `0 12px 32px rgba(79,63,245,0.45), 0 0 0 1px rgba(124,111,247,0.25)`
                      : `0 4px 18px rgba(79,63,245,0.30)`,
                  transition:     'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
                  letterSpacing:  '0.01em',
                }}
              >
                {submitting ? (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'esp-spin 0.8s linear infinite' }}>
                      <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"/>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>

          {/* ── Team Cards ──────────────────────────────────────────────────── */}
          <div
            ref={setRef('team')}
            style={{ display: 'flex', flexDirection: 'column', gap: '20px', ...reveal('team', 120) }}
          >
            {TEAM.map((member, i) => (
              <div
                key={i}
                onMouseEnter={() => setHoveredTeam(i)}
                onMouseLeave={() => setHoveredTeam(null)}
                style={{
                  ...glassCard(hoveredTeam === i),
                  padding:     '26px',
                  display:     'flex',
                  alignItems:  'center',
                  gap:         '22px',
                  cursor:      'default',
                  flexWrap:    isMobile ? 'wrap' : 'nowrap',
                  transform:   hoveredTeam === i ? 'translateY(-4px)' : 'translateY(0)',
                }}
              >
                {/* Avatar */}
                <div style={{
                  width:          '80px',
                  height:         '80px',
                  borderRadius:   '50%',
                  flexShrink:     0,
                  background:     member.photo ? 'transparent' : `linear-gradient(135deg, #003366, ${C.accent})`,
                  border:         `2.5px solid ${hoveredTeam === i
                    ? (isDark ? 'rgba(124,111,247,0.55)' : 'rgba(79,63,245,0.40)')
                    : (isDark ? 'rgba(79,140,247,0.30)' : 'rgba(79,140,247,0.22)')}`,
                  overflow:       'hidden',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  color:          '#fff',
                  fontSize:       '1.4rem',
                  fontWeight:     700,
                  boxShadow:      hoveredTeam === i
                    ? `0 0 0 4px ${isDark ? 'rgba(124,111,247,0.14)' : 'rgba(79,63,245,0.08)'}, 0 0 20px rgba(79,63,245,0.20)`
                    : 'none',
                  transition:     'border-color 0.22s ease, box-shadow 0.22s ease',
                }}>
                  {member.photo
                    ? <img src={member.photo} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : member.initials
                  }
                </div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <h3 style={{ margin: '0 0 4px', fontSize: '1.05rem', fontWeight: 800, color: C.text, letterSpacing: '-0.01em' }}>
                    {member.name}
                  </h3>
                  <div style={{
                    fontSize:             '0.82rem',
                    fontWeight:           700,
                    marginBottom:         '10px',
                    background:           `linear-gradient(90deg, ${C.accent}, ${C.accent2})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor:  'transparent',
                    backgroundClip:       'text',
                  }}>
                    {member.role}
                  </div>
                  <a
                    href={`mailto:${member.email}`}
                    style={{
                      display:    'inline-flex',
                      alignItems: 'center',
                      gap:        '6px',
                      color:      C.muted,
                      textDecoration: 'none',
                      fontSize:   '0.84rem',
                      transition: 'color 0.2s ease',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = C.accent; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = C.muted; }}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" {...SI}>
                      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                      <polyline points="22,6 12,13 2,6"/>
                    </svg>
                    {member.email}
                  </a>

                  {(member.linkedin || member.facebook) && (
                    <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                      {member.linkedin && (
                        <a
                          href={member.linkedin}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="LinkedIn"
                          style={{
                            width:          '32px',
                            height:         '32px',
                            borderRadius:   '50%',
                            background:     'rgba(79,140,247,0.10)',
                            border:         '1px solid rgba(79,140,247,0.22)',
                            display:        'flex',
                            alignItems:     'center',
                            justifyContent: 'center',
                            color:          C.accent,
                            textDecoration: 'none',
                            transition:     'background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
                          }}
                          onMouseEnter={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.background = C.accent;
                            el.style.color = '#fff';
                            el.style.boxShadow = `0 4px 14px rgba(79,63,245,0.35)`;
                          }}
                          onMouseLeave={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.background = 'rgba(79,140,247,0.10)';
                            el.style.color = C.accent;
                            el.style.boxShadow = 'none';
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                          </svg>
                        </a>
                      )}
                      {member.facebook && (
                        <a
                          href={member.facebook}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Facebook"
                          style={{
                            width:          '32px',
                            height:         '32px',
                            borderRadius:   '50%',
                            background:     'rgba(79,140,247,0.10)',
                            border:         '1px solid rgba(79,140,247,0.22)',
                            display:        'flex',
                            alignItems:     'center',
                            justifyContent: 'center',
                            color:          C.accent,
                            textDecoration: 'none',
                            transition:     'background 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
                          }}
                          onMouseEnter={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.background = C.accent;
                            el.style.color = '#fff';
                            el.style.boxShadow = `0 4px 14px rgba(79,63,245,0.35)`;
                          }}
                          onMouseLeave={e => {
                            const el = e.currentTarget as HTMLElement;
                            el.style.background = 'rgba(79,140,247,0.10)';
                            el.style.color = C.accent;
                            el.style.boxShadow = 'none';
                          }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                          </svg>
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}

            {/* Direct contact info */}
            <div style={{
              ...glassCard(),
              padding: '22px 26px',
              background: isDark
                ? 'rgba(79,63,245,0.06)'
                : 'rgba(79,63,245,0.03)',
              border: `1px solid ${isDark ? 'rgba(124,111,247,0.16)' : 'rgba(79,63,245,0.12)'}`,
            }}>
              <p style={{
                margin:        '0 0 14px',
                fontSize:      '0.78rem',
                color:         C.muted,
                fontWeight:    700,
                textTransform: 'uppercase',
                letterSpacing: '0.10em',
              }}>
                General Contact
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {[
                  { label: 'Email', value: 'start@sportenglish.com', href: 'mailto:start@sportenglish.com' },
                  { label: 'Phone', value: '+995 (551) 212-739',     href: 'tel:+995551212739'            },
                  { label: 'Based', value: 'Tbilisi, Georgia',       href: undefined                      },
                ].map((item, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{
                      fontSize:      '0.70rem',
                      color:         C.muted,
                      fontWeight:    700,
                      minWidth:      '40px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                    }}>
                      {item.label}
                    </span>
                    <span style={{
                      width:       '1px',
                      height:      '14px',
                      background:  isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
                      flexShrink:  0,
                    }} />
                    {item.href ? (
                      <a
                        href={item.href}
                        style={{
                          color:          C.accent,
                          textDecoration: 'none',
                          fontSize:       '0.9rem',
                          fontWeight:     600,
                          transition:     'opacity 0.2s ease',
                        }}
                        onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.75'; }}
                        onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; }}
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span style={{ color: C.muted, fontSize: '0.9rem' }}>{item.value}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* ── Info Grid ──────────────────────────────────────────────────────── */}
        <div
          ref={setRef('infoGrid')}
          style={{
            display:             'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, 1fr)',
            gap:                 '16px',
            ...reveal('infoGrid', 80),
          }}
        >
          {INFO_CARDS.map((card, i) => (
            <div
              key={i}
              onMouseEnter={() => setHoveredInfo(i)}
              onMouseLeave={() => setHoveredInfo(null)}
              style={{
                ...glassCard(hoveredInfo === i),
                borderRadius: '18px',
                padding:      isMobile ? '22px 16px' : '30px 24px',
                textAlign:    'center',
                transform:    hoveredInfo === i ? 'translateY(-5px)' : 'translateY(0)',
              }}
            >
              {/* Icon container */}
              <div style={{
                width:          '54px',
                height:         '54px',
                borderRadius:   '50%',
                background:     hoveredInfo === i
                  ? isDark
                    ? `rgba(79,63,245,0.18)`
                    : `rgba(79,63,245,0.10)`
                  : isDark
                    ? 'rgba(79,140,247,0.09)'
                    : 'rgba(79,140,247,0.07)',
                border:         `1px solid ${hoveredInfo === i
                  ? (isDark ? 'rgba(124,111,247,0.45)' : 'rgba(79,63,245,0.28)')
                  : (isDark ? 'rgba(79,140,247,0.18)' : 'rgba(79,140,247,0.16)')}`,
                display:        'flex',
                alignItems:     'center',
                justifyContent: 'center',
                color:          iconAccents[i] ?? C.accent,
                margin:         '0 auto 16px',
                boxShadow:      hoveredInfo === i
                  ? `0 0 20px ${isDark ? 'rgba(79,63,245,0.25)' : 'rgba(79,63,245,0.14)'}`
                  : 'none',
                transition:     'background 0.22s ease, border-color 0.22s ease, box-shadow 0.22s ease',
              }}>
                {card.icon}
              </div>

              <h4 style={{
                margin:        '0 0 9px',
                fontSize:      '0.97rem',
                fontWeight:    800,
                color:         C.text,
                letterSpacing: '-0.01em',
              }}>
                {card.title}
              </h4>
              <p style={{
                color:      C.muted,
                fontSize:   '0.87rem',
                lineHeight: 1.68,
                margin:     0,
                whiteSpace: 'pre-line',
              }}>
                {card.body}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}